import GetFetch from "../hooks/GetFetch";
//#region Endpoint URL consts
/**
 * @constant
 * @type {string}
 * @description Endpoint to get all TCM project records.
 */
const URL_tcmProject = '/v1/TCMProject';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get a TCM project record by ID. Replace {tcmProjectRecordId} with the actual ID.
 */
export const URL_tcmProjectById = '/v1/TCMProject/GetTCMProjectRecordById';

/**
 * @constant
 * @type {string}
 * @description Endpoint to edit a TCM project record (PUT).
 */
export const URL_tcmProjectEdit = '/v1/TCMProject/';

/**
 * @constant
 * @type {string}
 * @description Endpoint to add a new TCM project record (POST).
 */
export const URL_tcmProjectAdd = '/v1/TCMProject/';
/**
 * @constant
 * @type {string}
 * @description Endpoint to add a new TCM project record (POST) with no estimates.
 */
export const URL_tcmProjectAdd_no_estimates = '/v1/TCMProject/AddTCMProjectRecord';
/**
 * @constant
 * @type {string}
 * @description Endpoint to get all price book records.
 */
const URL_tcmPriceBook = '/v1/TCMPriceBook';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get a price book record by ID. Append {priceBookId} to the URL.
 */
const URL_tcmPriceBookById = '/v1/TCMPriceBook/GetPriceBookById/';

/**
 * @constant
 * @type {string}
 * @description Endpoint to edit a price book record (PUT).
 */
const URL_tcmPriceBookEditProjectType = '/v1/TCMPriceBook/EditProjectType';

/**
 * @constant
 * @type {string}
 * @description Endpoint to add a new price book record (POST).
 */
const URL_tcmPriceBookAdd = '/v1/TCMPriceBook/AddPriceBookRecord';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get all TCM cost estimate records.
 */
const URL_tcmCostEstimate = '/v1/TCMCostEstimate';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get a TCM cost estimate record by ID. Replace {tcmCostEstimateRecordId} with the actual ID.
 */
const URL_tcmCostEstimateByRecordId = '/v1/TCMCostEstimate/GetTCMCostEstimateRecordById/{tcmCostEstimateRecordId}';

/**
 * @constant
 * @type {string}
 * @description Endpoint to edit a TCM cost estimate record (PUT).
 */
const URL_tcmCostEstimateEdit = '/v1/TCMCostEstimate/EditTCMCostEstimateRecord';

/**
 * @constant
 * @type {string}
 * @description Endpoint to add a new TCM cost estimate record (POST).
 */
const URL_tcmCostEstimateAdd = '/v1/TCMCostEstimate/AddTCMCostEstimateRecord';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get all cost estimate detail records.
 */
const URL_tcmCostEstimateDetail = '/v1/TCMCostEstimateDetail';

/**
 * @constant
 * @type {string}
 * @description Endpoint to get a cost estimate detail record by ID. Append {CostEstimateDetailId} to the URL.
 */
const URL_tcmCostEstimateDetailById = '/v1/TCMCostEstimateDetail/GetCostEstimateDetailById/';

/**
 * @constant
 * @type {string}
 * @description Endpoint to edit a cost estimate detail record (PUT).
 */
const URL_tcmCostEstimateDetailEdit = '/v1/TCMCostEstimateDetail/EditCostEstimateDetail';

/**
 * @constant
 * @type {string}
 * @description Endpoint to add a new cost estimate detail record (POST).
 */
const URL_tcmCostEstimateDetailAdd = '/v1/TCMCostEstimateDetail/AddCostEstimateDetailRecord';

/**
 * @constant
 * @type {string}
 * @description Endpoint to region records (GET).
 */
const URL_regions = '/v1/Region';

/**
 * @constant
 * @type {string}
 * @description Endpoint to retrieve building records by {region} value (GET).
 */
const URL_buildingsByRegions = '/v1/Building/Region';

/**
 * @constant
 * @type {string}
 * @description Endpoint to retrieve building records by {buildingId} value (GET).
 */
const URL_buildingById = '/v1/Building';

/**
 * @constant
 * @type {string}
 * @description Endpoint to retrieve Estimate Type Records (GET).
 */
const URL_estimateType = '/v1/EstimateType';

/**
 * @constant
 * @type {string}
 * @description Endpoint to retrieve TCM Cost Estimate Status Records (GET).
 */
const URL_tcmCostEstimateStatuses = '/v1/TCMCostEstimateStatus';


//#endregion - URLs

/**
 * Fetches TCM PriceBook Records and maps the response to structured objects.
 * @returns {Promise<Array>} Array of mapped PriceBook objects.
 */
export const fetchTcmPriceBooks = async () => {
    try {
        const { Errors, Message, Success } = await GetFetch(URL_tcmPriceBook); // Use the correct endpoint

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch TCM Price Books.");
        }

        return Message.map(status => {
            // Destructure top-level fields
            const {
                Id,
                Name,
                Description,
                UnitOfIssue,
                UnitPrice,
                LaborPrice,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            } = status;
            return {
                Id,
                Name,
                Description,
                UnitOfIssue,
                UnitPrice,
                LaborPrice,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            };
        });
    } catch (error) {
        console.error("Error fetching TCM Cost Estimate Status:", error);
        throw error;
    }
};
/**
* Fetches TCM Cost Estimate Status Records and maps the response to structured objects.
* @returns {Promise<Array>} Array of mapped TCM Cost Estimate Status objects.
*/
export const fetchTcmCostEstimateStatuses = async () => {
    try {
        const { Errors, Message, Success } = await GetFetch(URL_tcmCostEstimateStatuses); // Use the correct endpoint

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch TCM Cost Estimate Status.");
        }

        return Message.map(status => {
            // Destructure top-level fields
            const {
                Id,
                TCMCostEstimateStatusId,
                Name,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            } = status;
            return {
                Id,
                TCMCostEstimateStatusId,
                Name,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            };
        });
    } catch (error) {
        console.error("Error fetching TCM Cost Estimate Status:", error);
        throw error;
    }
};
/**
 * Fetches Estimate Type Records and maps the response to structured objects.
 * @returns {Promise<Array>} Array of mapped estimate type objects.
 */
export const fetchEstimateTypeRecords = async () => {
    try {
        const { Errors, Message, Success } = await GetFetch(URL_estimateType); // Use the correct endpoint

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch Estimates Types.");
        }

        return Message.map(estimateType => {
            // Destructure top-level fields
            const {
                Id,
                Name,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            } = estimateType;
            return {
                Id,
                Name,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            };
        });
    } catch (error) {
        console.error("Error fetching Estimates Types:", error);
        throw error;
    }
};
/**
 * Fetches TCM Project Cost Estimates and maps the response to structured objects.
 * @returns {Promise<Array>} Array of mapped project cost estimate objects.
 */
export const fetchTcmProjectRecords = async () => {
    try {
        const { Errors, Message, Success } = await GetFetch(URL_tcmProject); // Use the correct endpoint

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch TCM Project Cost Estimates.");
        }

        return Message.map(project => {
            // Destructure top-level fields
            const {
                Id,
                BuildingId,
                Building = {},
                ProjectDescription,
                ProjectStartDate,
                ProjectEndDate,
                TCMCostEstimates = [],
                Customers = [],
                Comments,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
            } = project;

            // Map Building object
            const mappedBuilding = {
                ...Building,
                Location: Building.Location || {}
            };
            // console.log("HOOO", TCMCostEstimates);

            // Map Customers array
            const mappedCustomers = Customers.map(customer => ({
                ...customer,
                BureauAddress: customer.BureauAddress || {},
                Buildings: (customer.Buildings || []).map(b => ({
                    ...b,
                    Location: b.Location || {}
                })),
                BuildingByCustomer: (customer.BuildingByCustomer || []).map(bc => ({
                    ...bc,
                    Building: bc.Building || {},
                    Customer: bc.Customer || {},
                    OccupancyAgreement: bc.OccupancyAgreement || {}
                })),
                OccupancyAgreements: (customer.OccupancyAgreements || []).map(oa => ({
                    ...oa
                }))
            }));

            return {
                Id,
                BuildingId,
                ProjectDescription,
                ProjectStartDate,
                ProjectEndDate,
                Comments,
                CreatedBy,
                CreatedDate,
                UpdatedBy,
                UpdatedDate,
                Building: mappedBuilding,
                TCMCostEstimates: TCMCostEstimates,
                Customers: mappedCustomers
            };
        });
    } catch (error) {
        console.error("Error fetching TCM Project Cost Estimates:", error);
        throw error;
    }
};
/**
 * Fetches Region records and maps the response to an array of RegionNumber objects.
 * @returns {Promise<Array<{RegionNumber: number}>>} Array of region number objects.
 */
export const fetchRegionNumbers = async () => {
    try {
        const { Errors, Message, Success } = await GetFetch(URL_regions);

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch Region records.");
        }

        return (Message || []).map(region => ({
            RegionNumber: region.RegionNumber
        }));
    } catch (error) {
        console.error("Error fetching Region records:", error);
        throw error;
    }
};
/**
 * Fetches building records for a given region and maps the response to structured objects.
 * @param {number|string} regionId - The region identifier to query.
 * @returns {Promise<Array>} Array of mapped building objects.
 */
export const fetchBuildingRecordsByRegion = async (regionId) => {
    try {
        const endpoint = `${URL_buildingsByRegions}/${regionId}`;
        const { Errors, Message, Success } = await GetFetch(endpoint);

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch building records by region.");
        }

        return (Message || []).map(building => {
            const {
                BuildingId,
                SequenceNumber,
                Name,
                Status,
                Facility,
                RegionNumber,
                Location = {},
                RentUsageFactor,
                AdjustedRentSqFt,
                AdjustedUsageSqFt,
                AnsiRentSqFt,
                AnsiUsableSqFt,
                JointUseSqFt,
                CommunityJointUseSqFt,
                LeaseJointUseSqFt,
                CongressionalRep,
                Version
            } = building;

            // Map Location object

            const mappedLocation = Location ?
                {
                    Id: Location.Id,
                    Address1: Location.Address1,
                    Address2: Location.Address2,
                    CityName: Location.CityName,
                    State: Location.State,
                    ZipCode: Location.ZipCode
                }
                :
                {

                };

            return {
                BuildingId,
                SequenceNumber,
                Name,
                Status,
                Facility,
                RegionNumber,
                Location: mappedLocation,
                RentUsageFactor,
                AdjustedRentSqFt,
                AdjustedUsageSqFt,
                AnsiRentSqFt,
                AnsiUsableSqFt,
                JointUseSqFt,
                CommunityJointUseSqFt,
                LeaseJointUseSqFt,
                CongressionalRep,
                Version
            };
        });
    } catch (error) {
        console.error("Error fetching building records by region:", error);
        throw error;
    }
};
/**
 * Fetches building record by BuildingId and maps the response to structured object.
 * @param {number} buildingId - The region identifier to query.
 * @returns {Promise<Array>} Array of mapped building objects.
 */
export const fetchBuildingRecordById = async (buildingId) => {
    try {
        const endpoint = `${URL_buildingById}/${buildingId}`;
        const { Errors, Message, Success } = await GetFetch(endpoint);

        if (!Success) {
            throw new Error(Errors?.[0]?.Message || "Failed to fetch building record by specificed Building Id.");
        }

        // Message is a single object
        const building = Message || {};

        const {
            BuildingId,
            SequenceNumber,
            Name,
            Status,
            Facility,
            RegionNumber,
            Location = {},
            RentUsageFactor,
            AdjustedRentSqFt,
            AdjustedUsageSqFt,
            AnsiRentSqFt,
            AnsiUsableSqFt,
            JointUseSqFt,
            CommunityJointUseSqFt,
            LeaseJointUseSqFt,
            CongressionalRep,
            Version
        } = building;

        // Map Location object
        const mappedLocation = {
            Id: Location.Id,
            Address1: Location.Address1,
            Address2: Location.Address2,
            CityName: Location.CityName,
            State: Location.State,
            ZipCode: Location.ZipCode
        };

        return {
            BuildingId,
            SequenceNumber,
            Name,
            Status,
            Facility,
            RegionNumber,
            Location: mappedLocation,
            RentUsageFactor,
            AdjustedRentSqFt,
            AdjustedUsageSqFt,
            AnsiRentSqFt,
            AnsiUsableSqFt,
            JointUseSqFt,
            CommunityJointUseSqFt,
            LeaseJointUseSqFt,
            CongressionalRep,
            Version
        };
    } catch (error) {
        console.error("Error fetching building record by ID:", error);
        throw error;
    }
};